/**
 * Authored clip → ClipData — the timeline slice's playback projection
 * (ADR-006 §13.4, ADR-007 §5.2). The keys the `key.*` verbs commit become the
 * clip the mixer SAMPLES: undoing a key.set yields a Document without that
 * key, this conversion yields a clip without it, and there is no second store
 * left to re-assert it (§5.2 rule 4 — the trap's mechanism removed, extended
 * to time). Pure data reshaping: interpolation between keys belongs to the
 * engine's sampler (three slerps rotation keys natively), never re-implemented
 * here.
 * Depends on: ir/clip-data (the gate), session/document (types).
 * Depended on by: the Document driver, the app's presentation resolver.
 */
import { parseClipData, type ClipData } from '../ir/clip-data.js';
import { CommandRefusal } from '../ir/errors.js';
import type { AuthoredClip } from './document.js';

/** Whether any track carries a key — the "is there anything to play?" fact. */
export function hasPlayableKeys(clip: AuthoredClip): boolean {
  return clip.tracks.some((t) => t.keys.length > 0);
}

/**
 * Whether two authored clips present IDENTICAL playback — the fact the
 * presentation resolver needs, because reference identity cannot carry it:
 * every applyCommand reparses the Document and zod rebuilds ALL clip objects,
 * so after an UNRELATED edit the active clip is a new object with the same
 * content, and re-presenting on it would stop the mixer and reset playback to
 * zero (Codex P2, #61 r4). Compares exactly what authoredClipData samples:
 * name, track bones in order, and each key's time and rotation. Key ids and
 * the §12.2 counter are identity bookkeeping, not playback — two clips that
 * differ only there sample identically and ARE the same presentation.
 */
export function playbackEquals(a: AuthoredClip, b: AuthoredClip): boolean {
  if (a === b) return true; // undo/redo re-points at the retained frozen object
  if (a.name !== b.name || a.tracks.length !== b.tracks.length) return false;
  return a.tracks.every((ta, i) => {
    const tb = b.tracks[i] as AuthoredClip['tracks'][number];
    return (
      ta.bone === tb.bone &&
      ta.keys.length === tb.keys.length &&
      ta.keys.every((ka, j) => {
        const kb = tb.keys[j] as (typeof ta.keys)[number];
        return (
          ka.time === kb.time &&
          ka.rotation.x === kb.rotation.x &&
          ka.rotation.y === kb.rotation.y &&
          ka.rotation.z === kb.rotation.z &&
          ka.rotation.w === kb.rotation.w
        );
      })
    );
  });
}

/**
 * How long an authored clip PLAYS: the last key's time, floored at ONE FRAME
 * (1/fps) — a declaration, not a guess. A single key at 0s is autokey's first
 * gesture and plays as a held frame rather than as a zero-length clip.
 *
 * Exported because a surface that draws the timeline must measure it the same
 * way the mixer does. A strip that invented its own floor drew a playhead
 * wrapping out of phase with the clip actually being sampled — honest-looking
 * motion over a lie (Codex P2, #63). One duration, one source.
 */
export function authoredClipDurationSec(clip: AuthoredClip, fps: number): number {
  let lastKeyTime = 0;
  for (const track of clip.tracks) {
    for (const key of track.keys) {
      if (key.time > lastKeyTime) lastKeyTime = key.time;
    }
  }
  return Math.max(lastKeyTime, 1 / fps);
}

/**
 * Convert an authored clip into engine-neutral ClipData at the Document's
 * declared fps. Tracks with zero keys carry no content (they persist only to
 * hold the §12.2 counter) and produce no ClipTrack; a clip with NO keys at
 * all refuses loudly — nothing to play is a fact the caller must handle, not
 * a silent empty clip (§8). Duration comes from authoredClipDurationSec, the
 * one measure the timeline surface shares.
 */
export function authoredClipData(clip: AuthoredClip, fps: number): ClipData {
  const populated = clip.tracks.filter((t) => t.keys.length > 0);
  if (populated.length === 0) {
    throw new CommandRefusal(
      `authored clip "${clip.name}" has no keys — nothing to play (§8: key something, or do not present it).`,
    );
  }
  const tracks = populated.map((track) => {
    const times = new Float32Array(track.keys.length);
    const values = new Float32Array(track.keys.length * 4);
    track.keys.forEach((key, i) => {
      times[i] = key.time;
      values[i * 4] = key.rotation.x;
      values[i * 4 + 1] = key.rotation.y;
      values[i * 4 + 2] = key.rotation.z;
      values[i * 4 + 3] = key.rotation.w;
    });
    return { target: track.bone, path: 'rotation' as const, times, values };
  });
  return parseClipData({
    name: clip.name,
    fps,
    durationSec: authoredClipDurationSec(clip, fps),
    additive: false,
    rootMotionTarget: null, // rotation-only in v1 — no translation channel exists to carry root motion
    tracks,
  });
}
